//xasino-frontend/src/pages/AgentDetails.tsx
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Users,
  Eye,
  Phone,
  Mail,
  MapPin,
  Activity,
  X,
} from "lucide-react";
import {
  BodyLargeBold,
  BodyNormalBold,
  BodySmallMedium,
  BodySmallRegular,
} from "../components/ui/Typography";
import { agentService } from "../api/services/agents/agentService";
import { gameRecordService } from "../api/services/gameRecords/gameRecordsService";
import { customerService } from "../api/services/customers/customerService";
import { Agent } from "../api/services/agents/type";
import { Customer } from "../types/customer";
import { GameRecord } from "../api/services/gameRecords/type";
import toast from "react-hot-toast";
import { AdvancedPlayerAssignmentModal } from "../components/commission/AdvancedPlayerAssignmentModal";

import {
  commissionService,
  AgentPlayerAssignment,
} from "../api/services/commission/commissionService";

interface PlayerWithRecords {
  id: string;
  name: string;
  username: string;
  email: string;
  phone: string;
  customerType: string;
  totalWin: number;
  totalLoss: number;
  netProfit: number;
  totalGames: number;
  lastPlayDate: string;
  status: "active" | "inactive";
}

interface AgentStats {
  totalPlayers: number;
  activePlayers: number;
  totalWins: number;
  totalLosses: number;
  netProfit: number;
}

export const AgentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [agent, setAgent] = useState<Agent | null>(null);
  const [players, setPlayers] = useState<PlayerWithRecords[]>([]);
  const [stats, setStats] = useState<AgentStats>({
    totalPlayers: 0,
    activePlayers: 0,
    totalWins: 0,
    totalLosses: 0,
    netProfit: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [playerRecords, setPlayerRecords] = useState<GameRecord[]>([]);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState<boolean>(false);
  const [assignments, setAssignments] = useState<AgentPlayerAssignment[]>([]);

  const [summaryData, setSummaryData] = useState({
    totalPlayers: 0,
    activePlayers: 0,
    packageGroups: {},
    totalCommissionEarned: 0,
    pendingCommission: 0,
  });
  useEffect(() => {
    if (id) {
      fetchAgentDetails();
      fetchAgentPlayers();
    }
  }, [id]);

  console.log("assignments", assignments, summaryData);

  const fetchAgentDetails = async () => {
    try {
      const response = await agentService.getAgent(id!);
      setAgent(response);
    } catch (error) {
      console.error("Error fetching agent details:", error);
      toast.error("Failed to fetch agent details");
    }
  };

  const fetchAgentPlayers = async () => {
    setLoading(true);
    try {
      // First, get list of customers associated with this agent
      // We need to fetch customers who have played games managed by this agent
      const customersResponse = await customerService.getCustomers({
        limit: 100,
      });

      const customers = customersResponse.items || [];
      const customerInfoMap = new Map<string, Customer>();

      // Store customer info for quick lookup
      customers.forEach((customer) => {
        customerInfoMap.set(customer.id, customer);
      });

      // Calculate dates for filtering (today only or date range as needed)
      const today = new Date();
      const formatDate = (date: Date) => date.toISOString().split("T")[0];
      const todayStr = formatDate(today);

      // Fetch game records for all customers for today (or date range)
      const gameRecordsPromises = customers.map(async (customer) => {
        try {
          const records = await gameRecordService.getGameRecords({
            customerId: customer.id,
            dateFrom: todayStr,
            dateTo: todayStr,
            limit: 100,
          });
          return {
            customerId: customer.id,
            records: records.gameRecords || [],
          };
        } catch (error) {
          console.error(
            `Error fetching game records for ${customer.id}:`,
            error,
          );
          return { customerId: customer.id, records: [] };
        }
      });

      const allGameRecords = await Promise.all(gameRecordsPromises);

      // Calculate statistics for each player based on game records
      const playersWithStats: PlayerWithRecords[] = [];

      for (const { customerId, records } of allGameRecords) {
        const customerInfo = customerInfoMap.get(customerId);
        if (!customerInfo || records.length === 0) continue;

        // Calculate total wins and losses from game records
        const totalWin = records.reduce(
          (sum: number, r: GameRecord) => sum + (r.winAmount || 0),
          0,
        );
        const totalLoss = records.reduce(
          (sum: number, r: GameRecord) => sum + (r.lossAmount || 0),
          0,
        );
        const totalGames = records.length;

        // Get the most recent game date
        const sortedRecords = [...records].sort(
          (a: GameRecord, b: GameRecord) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        const lastGame = sortedRecords[0];

        playersWithStats.push({
          id: customerId,
          name: customerInfo.name,
          username: customerInfo.playerId,
          email: customerInfo.email,
          phone: customerInfo.phone,
          customerType: customerInfo.customerType,
          totalWin,
          totalLoss,
          netProfit: totalWin - totalLoss,
          totalGames,
          lastPlayDate: lastGame?.createdAt || "Never",
          status: "active",
        });
      }

      const validPlayers = playersWithStats;

      setPlayers(validPlayers);

      // Calculate agent statistics
      const totalWins = validPlayers.reduce((sum, p) => sum + p.totalWin, 0);
      const totalLosses = validPlayers.reduce((sum, p) => sum + p.totalLoss, 0);
      const netProfit = totalWins - totalLosses;
      const activePlayers = validPlayers.filter((p) => p.totalGames > 0).length;

      setStats({
        totalPlayers: validPlayers.length,
        activePlayers,
        totalWins,
        totalLosses,
        netProfit,
      });
    } catch (error) {
      console.error("Error fetching agent players:", error);
      toast.error("Failed to fetch player data");
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = useCallback(async () => {
    try {
      setLoading(true);

      // Load assignments
      const assignmentsData = await commissionService.getAgentAssignments(id!);
      setAssignments(assignmentsData);

      // Load summary data
      const summary = await commissionService.getCommissionSummary(id!);
      setSummaryData({
        totalPlayers: summary.totalPlayers,
        activePlayers: summary.activePlayers,
        packageGroups: summary.packageGroups,
        totalCommissionEarned: summary.totalCommissionEarned || 0,
        pendingCommission: summary.pendingCommission || 0,
      });
    } catch (error) {
      console.error("Error loading commission data:", error);
      toast.error("Failed to load commission data");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleAssignmentCreated = () => {
    setAssignModalOpen(false);
    loadAssignments();
    toast.success("Player assignment created successfully");
  };

  const fetchPlayerGameRecords = async (playerId: string) => {
    setRecordsLoading(true);
    try {
      const response = await gameRecordService.getGameRecords({
        customerId: playerId,
        limit: 50,
      });
      setPlayerRecords(response.gameRecords || []);
    } catch (error) {
      console.error("Error fetching player game records:", error);
      toast.error("Failed to fetch game records");
    } finally {
      setRecordsLoading(false);
    }
  };

  const handleViewPlayerRecords = (playerId: string) => {
    setSelectedPlayer(playerId);
    fetchPlayerGameRecords(playerId);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="p-6 bg-background min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/agents")}
            className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-400" />
          </button>
          <div>
            <BodyLargeBold className="text-white">Agent Details</BodyLargeBold>
            {agent && (
              <BodySmallRegular className="text-gray-400">
                {agent.name}
              </BodySmallRegular>
            )}
          </div>
        </div>
        <div>
          <button
            onClick={() => setAssignModalOpen(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
          >
            Assign Player
          </button>
        </div>
      </div>

      {/* Agent Info Card */}
      {agent && (
        <div className="bg-gray-800 rounded-lg p-6 mb-6 border border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <BodySmallRegular className="text-gray-400 mb-2">
                Contact Information
              </BodySmallRegular>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-500" />
                  <BodySmallMedium className="text-white">
                    {agent.phone || "N/A"}
                  </BodySmallMedium>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-500" />
                  <BodySmallMedium className="text-white">
                    {agent.email || "N/A"}
                  </BodySmallMedium>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gray-500" />
                  <BodySmallMedium className="text-white">
                    {"N/A"}
                  </BodySmallMedium>
                </div>
              </div>
            </div>

            <div>
              <BodySmallRegular className="text-gray-400 mb-2">
                Commission Details
              </BodySmallRegular>
              <div className="space-y-2">
                <div>
                  <BodySmallRegular className="text-gray-500">
                    Rate
                  </BodySmallRegular>
                  <BodyNormalBold className="text-green-400">
                    {agent.commissionPercent || 0}%
                  </BodyNormalBold>
                </div>
              </div>
            </div>

            <div>
              <BodySmallRegular className="text-gray-400 mb-2">
                Status
              </BodySmallRegular>
              <div className="space-y-2">
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium ${
                    agent.agentType === "NORMAL"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {agent.agentType === "NORMAL" ? "Normal" : "Junket"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                Total Players
              </BodySmallRegular>
              <BodyNormalBold className="text-white mt-1">
                {stats.totalPlayers}
              </BodyNormalBold>
            </div>
            <Users className="w-8 h-8 text-primary" />
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                Active Players
              </BodySmallRegular>
              <BodyNormalBold className="text-green-400 mt-1">
                {stats.activePlayers}
              </BodyNormalBold>
            </div>
            <Activity className="w-8 h-8 text-green-400" />
          </div>
        </div>

        {/*
         * TODO:  lets enable later if required
         */}
        {/* <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                Total Wins
              </BodySmallRegular>
              <BodyNormalBold className="text-green-400 mt-1">
                {formatCurrency(stats.totalWins)}
              </BodyNormalBold>
            </div>
            <TrendingUp className="w-8 h-8 text-green-400" />
          </div>
        </div> */}

        {/* <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                Total Losses
              </BodySmallRegular>
              <BodyNormalBold className="text-red-400 mt-1">
                {formatCurrency(stats.totalLosses)}
              </BodyNormalBold>
            </div>
            <DollarSign className="w-8 h-8 text-red-400" />
          </div>
        </div> */}

        {/* <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <BodySmallRegular className="text-gray-400">
                Net Profit
              </BodySmallRegular>
              <BodyNormalBold
                className={
                  stats.netProfit >= 0
                    ? "text-blue-400 mt-1"
                    : "text-red-400 mt-1"
                }
              >
                {formatCurrency(stats.netProfit)}
              </BodyNormalBold>
            </div>
            <Calendar className="w-8 h-8 text-blue-400" />
          </div>
        </div> */}
      </div>

      {/* Players Table */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
        <div className="p-4 border-b border-gray-700">
          <BodyNormalBold className="text-white">
            Associated Players
          </BodyNormalBold>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Player
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Username
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Type
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Total Wins
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Total Losses
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Net Profit
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Last Played
                  </BodySmallMedium>
                </th>
                <th className="px-4 py-3 text-left">
                  <BodySmallMedium className="text-gray-400">
                    Actions
                  </BodySmallMedium>
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <BodySmallRegular className="text-gray-400">
                      Loading players...
                    </BodySmallRegular>
                  </td>
                </tr>
              ) : players.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center">
                    <BodySmallRegular className="text-gray-400">
                      No players found for this agent
                    </BodySmallRegular>
                  </td>
                </tr>
              ) : (
                players.map((player) => (
                  <tr
                    key={player.id}
                    className="border-b border-gray-700 hover:bg-gray-700/50"
                  >
                    <td className="px-4 py-3">
                      <BodySmallMedium className="text-white">
                        {player.name}
                      </BodySmallMedium>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallRegular className="text-gray-300">
                        {player.username}
                      </BodySmallRegular>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 rounded text-xs ${
                          player.customerType === "vip"
                            ? "bg-yellow-100 text-yellow-800"
                            : player.customerType === "vvip"
                              ? "bg-purple-100 text-purple-800"
                              : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {player.customerType.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallMedium className="text-green-400">
                        {formatCurrency(player.totalWin)}
                      </BodySmallMedium>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallMedium className="text-red-400">
                        {formatCurrency(player.totalLoss)}
                      </BodySmallMedium>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallMedium
                        className={
                          player.netProfit >= 0
                            ? "text-green-400"
                            : "text-red-400"
                        }
                      >
                        {formatCurrency(player.netProfit)}
                      </BodySmallMedium>
                    </td>
                    <td className="px-4 py-3">
                      <BodySmallRegular className="text-gray-400">
                        {formatDate(player.lastPlayDate)}
                      </BodySmallRegular>
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => handleViewPlayerRecords(player.id)}
                        className="p-1 hover:bg-gray-600 rounded transition-colors"
                        title="View Game Records"
                      >
                        <Eye className="w-4 h-4 text-gray-400" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Player Cash Records Modal */}
      {selectedPlayer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-700 flex items-center justify-between">
              <BodyNormalBold className="text-white">
                Game Records -{" "}
                {players.find((p) => p.id === selectedPlayer)?.name}
              </BodyNormalBold>
              <button
                onClick={() => {
                  setSelectedPlayer(null);
                  setPlayerRecords([]);
                }}
                className="p-2 hover:bg-gray-700 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="overflow-y-auto max-h-[60vh]">
              <table className="w-full">
                <thead className="bg-gray-900 border-b border-gray-700 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <BodySmallMedium className="text-gray-400">
                        Table
                      </BodySmallMedium>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <BodySmallMedium className="text-gray-400">
                        Bet Amount
                      </BodySmallMedium>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <BodySmallMedium className="text-gray-400">
                        Win Amount
                      </BodySmallMedium>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <BodySmallMedium className="text-gray-400">
                        Loss Amount
                      </BodySmallMedium>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <BodySmallMedium className="text-gray-400">
                        Shift
                      </BodySmallMedium>
                    </th>
                    <th className="px-4 py-3 text-left">
                      <BodySmallMedium className="text-gray-400">
                        Date
                      </BodySmallMedium>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {recordsLoading ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <BodySmallRegular className="text-gray-400">
                          Loading records...
                        </BodySmallRegular>
                      </td>
                    </tr>
                  ) : playerRecords.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center">
                        <BodySmallRegular className="text-gray-400">
                          No game records found
                        </BodySmallRegular>
                      </td>
                    </tr>
                  ) : (
                    playerRecords.map((record) => (
                      <tr
                        key={record.id}
                        className="border-b border-gray-700 hover:bg-gray-700/50"
                      >
                        <td className="px-4 py-3">
                          <BodySmallRegular className="text-gray-300">
                            Table {record.gameTableId || "N/A"}
                          </BodySmallRegular>
                        </td>
                        <td className="px-4 py-3">
                          <BodySmallMedium className="text-yellow-400">
                            {formatCurrency(record.betAmount || 0)}
                          </BodySmallMedium>
                        </td>
                        <td className="px-4 py-3">
                          <BodySmallMedium className="text-green-400">
                            {formatCurrency(record.winAmount || 0)}
                          </BodySmallMedium>
                        </td>
                        <td className="px-4 py-3">
                          <BodySmallMedium className="text-red-400">
                            {formatCurrency(record.lossAmount || 0)}
                          </BodySmallMedium>
                        </td>
                        <td className="px-4 py-3">
                          <BodySmallRegular className="text-gray-300">
                            {record.shift || "N/A"}
                          </BodySmallRegular>
                        </td>
                        <td className="px-4 py-3">
                          <BodySmallRegular className="text-gray-400">
                            {formatDate(record.createdAt)}
                          </BodySmallRegular>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {assignModalOpen && (
        <AdvancedPlayerAssignmentModal
          isOpen={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          onSuccess={handleAssignmentCreated}
          agentId={id!}
        />
      )}
    </div>
  );
};
